import { z } from 'zod';
import {
  RequestCarrierBidSchema,
  RequestCarrierSchema,
  type CarrierBidCurrency,
  type RequestCarrier,
  type RequestCarrierBid,
  type RequestCarrierBidUnit,
} from '@magimp/shared';
import { request } from '../client.js';

// WHY (#702 / #1057): per-destination scope on STANDARD requests
//   (`arrivalCityId` is a UUID); request-wide scope on CONTAINER
//   requests (`arrivalCityId` is `null`). Backend rejects mismatched
//   pairs — a UUID on a CONTAINER request or `null` on a STANDARD
//   request — with `CarrierCityNotInRequestError`.
export interface AddCarrierBody {
  carrierId: string;
  arrivalCityId: string | null;
}
// WHY (#569): carrier patch is now strictly a state-change envelope.
//   `REJECTED` requires a `rejectReason`; `PENDING` resets the carrier
//   (and wipes any bids on it). Bid CRUD lives on its own endpoints.
export interface PatchCarrierBody {
  state: RequestCarrier['state'];
  rejectReason?: string;
}

export function addCarrier(id: string, body: AddCarrierBody): Promise<RequestCarrier> {
  return request<RequestCarrier>(
    `/requests/${encodeURIComponent(id)}/carriers`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestCarrierSchema,
  );
}

// WHY (#702): routes now key by the server-minted `RequestCarrier.id`
//   (`requestCarrierId`) instead of the catalog `carrierId`. The same
//   carrier may appear in multiple destinations of one request, so
//   the catalog id alone is no longer a unique row identifier.
export function patchCarrier(
  id: string,
  requestCarrierId: string,
  body: PatchCarrierBody,
): Promise<RequestCarrier> {
  return request<RequestCarrier>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestCarrierSchema,
  );
}

export function deleteCarrier(id: string, requestCarrierId: string): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

// WHY (#569): one carrier may submit multiple bids per request. Bid CRUD
//   lives under the carrier resource so the URL reflects ownership; the
//   service re-asserts `(requestId, requestCarrierId, bidId)` consistency
//   on every call (denormalised parent ids on the bid row).
export interface UpsertBidBody {
  pricePerUnit: number;
  currency: CarrierBidCurrency;
  unit: RequestCarrierBidUnit;
  // WHY (#1051): transit-time range; both must be set together or both
  //   omitted/null. The DTO enforces `daysMin <= daysMax` and positive
  //   integer values.
  daysMin?: number | null;
  daysMax?: number | null;
  comment?: string;
  // WHY (#585): validity window in whole days, anchored at the
  //   server-side `quotedAt`. Optional on the wire — the DTO defaults
  //   to 14 when omitted — so callers that don't care can keep the
  //   bare contract.
  validityDays?: number;
}

export function addCarrierBid(
  id: string,
  requestCarrierId: string,
  body: UpsertBidBody,
): Promise<RequestCarrierBid> {
  return request<RequestCarrierBid>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(requestCarrierId)}/bids`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestCarrierBidSchema,
  );
}

export function patchCarrierBid(
  id: string,
  requestCarrierId: string,
  bidId: string,
  body: UpsertBidBody,
): Promise<RequestCarrierBid> {
  return request<RequestCarrierBid>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(
      requestCarrierId,
    )}/bids/${encodeURIComponent(bidId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RequestCarrierBidSchema,
  );
}

export function deleteCarrierBid(
  id: string,
  requestCarrierId: string,
  bidId: string,
): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/carriers/${encodeURIComponent(
      requestCarrierId,
    )}/bids/${encodeURIComponent(bidId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

export function pickWinner(id: string, bidId: string): Promise<void> {
  return request<void>(
    `/requests/${encodeURIComponent(id)}/winner`,
    {
      method: 'POST',
      body: JSON.stringify({ bidId }),
      headers: { 'content-type': 'application/json' },
    },
    {},
    z.void(),
  );
}
